import type { WorkItem } from '@/api/work';
import { recordPath } from '@/ui';

import { dealStep, fallbackRow } from './planParts';
import type { RowBuilder, SectionPlan } from './planTypes';
import { dueClause, moneyOf, quoted, withNote, words } from './rowText';

export type SalesSection =
  | 'SAFETY'
  | 'APPROVALS'
  | 'LEADS_TO_ROUTE'
  | 'DEALS_OVERDUE'
  | 'DEALS_DUE_TODAY'
  | 'CONFIRM_SALE'
  | 'PRESENT_PROPOSAL'
  | 'HAND_OFF'
  | 'MARK_INSTALLED'
  | 'FOLLOW_UPS'
  | 'TECH_HANDOFFS'
  | 'TECH_REVIEWS';

const PRICE_EXCEPTIONS = '/proposals/approvals';
const TIME_OFF_REQUESTS = '/team';

/** The queue opened at the request the item is about, which is the first of its set. */
function exceptionPath(item: WorkItem): string {
  return `${PRICE_EXCEPTIONS}?${new URLSearchParams({ request: item.recordId }).toString()}`;
}

const approvalRow: RowBuilder = (item, context) => {
  if (item.recordType === 'TIME_OFF') {
    return {
      subject: item.title,
      predicate: withNote('asked for time off', 'noting', item.detail),
      action: { label: 'Review request', to: TIME_OFF_REQUESTS },
    };
  }
  if (item.recordType !== 'PRICE_EXCEPTION') {
    return fallbackRow(item);
  }
  const amount = moneyOf(item, context);
  const asked =
    item.personName === null
      ? 'price exception requested'
      : `${item.personName} asked for a price exception`;
  return {
    subject: item.title,
    predicate: words(asked, amount === null ? null : `at ${amount}`),
    action: { label: 'Review request', to: exceptionPath(item) },
  };
};

/** Time off is decided on the team page, exceptions on their own: the list follows the items. */
function approvalsList(items: readonly WorkItem[]): string {
  const onlyTimeOff = items.length > 0 && items.every((item) => item.recordType === 'TIME_OFF');
  return onlyTimeOff ? TIME_OFF_REQUESTS : PRICE_EXCEPTIONS;
}

/** A deal whose next step has a day on it; `fallback` says it when the server sent no day. */
function dealDueRow(fallback: string): RowBuilder {
  return (item, context) => ({
    subject: item.title,
    predicate: words('next step', quoted(item.detail), dueClause(item, context) ?? fallback),
    action: { label: 'Open deal', to: dealStep(item, 'overview', '/pipeline') },
  });
}

const handOffRow: RowBuilder = (item) => {
  if (item.recordType === 'HANDOFF_PACKAGE') {
    return {
      subject: item.title,
      predicate: 'installation package has open blockers',
      action: { label: 'Clear blockers', to: dealStep(item, 'installation', '/handoff') },
    };
  }
  return {
    subject: item.title,
    predicate: 'sold, with no installation package yet',
    action: { label: 'Start handoff', to: dealStep(item, 'installation', '/handoff') },
  };
};

// The item is a proposal, so its `state` is the proposal's status and not the exception's: the
// sentence says a decision was made and leaves what it was to the proposal's own tab.
const presentProposalRow: RowBuilder = (item) => ({
  subject: item.title,
  predicate: 'pricing exception decided, proposal not yet presented',
  action: {
    label: 'Open proposal',
    to: dealStep(item, 'proposal', recordPath('proposal', item.recordId)),
  },
});

/** The sections of work that belong to selling: leads, deals, proposals, handoffs and aftercare. */
export const SALES_PLANS: Readonly<Record<SalesSection, SectionPlan>> = {
  SAFETY: {
    title: 'Safety concerns',
    seeAllTo: () => '/leads',
    row: (item) => ({
      subject: item.title,
      predicate: withNote('answered yes to a safety question', 'asking about', item.detail),
      action: { label: 'Review lead', to: recordPath('lead', item.recordId) },
    }),
  },
  APPROVALS: {
    title: 'Approvals waiting for you',
    seeAllTo: approvalsList,
    row: approvalRow,
  },
  LEADS_TO_ROUTE: {
    title: 'Leads to route',
    seeAllTo: () => '/leads',
    row: (item) => ({
      subject: item.title,
      predicate: withNote('waiting to be routed', 'asking about', item.detail),
      action: { label: 'Route lead', to: recordPath('lead', item.recordId) },
    }),
  },
  DEALS_OVERDUE: {
    title: 'Deals past due',
    seeAllTo: () => '/pipeline',
    row: dealDueRow('is overdue'),
  },
  DEALS_DUE_TODAY: {
    title: 'Deals due today',
    seeAllTo: () => '/pipeline',
    row: dealDueRow('is due today'),
  },
  CONFIRM_SALE: {
    title: 'Sales to confirm',
    seeAllTo: () => '/pipeline',
    row: (item) => ({
      subject: item.title,
      predicate: 'ready for you to confirm the sale',
      action: { label: 'Confirm sale', to: dealStep(item, 'decision', '/pipeline') },
    }),
  },
  PRESENT_PROPOSAL: {
    title: 'Proposals to present',
    seeAllTo: () => '/proposals',
    row: presentProposalRow,
  },
  HAND_OFF: {
    title: 'Sold deals to hand off',
    seeAllTo: () => '/handoff',
    row: handOffRow,
  },
  MARK_INSTALLED: {
    title: 'Installations to close out',
    seeAllTo: () => '/handoff',
    row: (item) => ({
      subject: item.title,
      predicate: 'installation job is finished, so the deal can be marked installed',
      action: { label: 'Mark installed', to: dealStep(item, 'installation', '/handoff') },
    }),
  },
  FOLLOW_UPS: {
    title: 'Follow-ups due',
    seeAllTo: () => '/followup',
    row: (item, context) => ({
      subject: item.title,
      predicate: words('follow-up', quoted(item.detail), dueClause(item, context) ?? 'is due'),
      action: { label: 'Follow up', to: dealStep(item, 'follow-up', '/followup') },
    }),
  },
  TECH_HANDOFFS: {
    title: 'Technician handoffs to route',
    seeAllTo: () => '/replacement',
    row: (item) => ({
      subject: item.title,
      predicate:
        item.personName === null
          ? 'replacement handoff waiting to be routed'
          : `${item.personName} raised a replacement handoff that needs routing`,
      action: { label: 'Route handoff', to: '/replacement' },
    }),
  },
  TECH_REVIEWS: {
    title: 'Consultations to review',
    seeAllTo: () => '/jobs/consultation-tasks',
    row: (item) => ({
      subject: item.title,
      predicate: withNote('consultation waiting for your review', 'noting', item.detail),
      action: {
        label: 'Review consultation',
        to: dealStep(item, 'consultation', '/jobs/consultation-tasks'),
      },
    }),
  },
};
